import React, { useEffect, useState } from "react";
import {
  CheckCircle2Icon,
  ExternalLinkIcon,
  Loader2Icon,
  MailIcon,
} from "lucide-react";
import { Button } from "@/shadcn/components/ui/button";
import { Card, CardContent, CardHeader } from "@/shadcn/components/ui/card";
import { useQuery } from "@/hooks/useQuery";

function pickApiBase() {
  const explicit = import.meta.env.VITE_CHECKOUT_API_URL;
  const dev = import.meta.env.VITE_DEV_CF_API_URL;
  const staging = import.meta.env.VITE_STAGING_CF_API_URL;
  const stable = import.meta.env.VITE_STABLE_CF_API_URL;
  const forceStaging =
    import.meta.env.VITE_USE_STAGING_API === "true" ||
    import.meta.env.VITE_USE_STAGING_API === true;

  if (explicit) return explicit;
  if (import.meta.env.DEV && dev) return dev;
  if (!import.meta.env.DEV && forceStaging) return staging || dev || stable;
  return stable || dev || "https://cf.api-atomicpay.com/api";
}

const THANK_YOU_CONTENT = {
  pt: {
    title: "Compra Aprovada!",
    subtitle: "Seu pagamento foi processado com sucesso",
    confirmationTitle: "Confirmação Enviada",
    confirmationDescription:
      "Um e-mail de confirmação com todos os detalhes da sua compra foi enviado para o endereço cadastrado. Verifique sua caixa de entrada nos próximos minutos.",
    nextStepsTitle: "Próximos Passos:",
    nextSteps: [
      "Verifique seu e-mail para a confirmação detalhada do pedido",
      "Acompanhe o status do seu pedido através do link enviado",
      "Em caso de dúvidas, nossa equipe de suporte está à disposição",
    ],
    closing:
      "Obrigado por sua compra! Esperamos que tenha uma excelente experiência.",
    loadingAccess: "Carregando seu acesso...",
    approvedFor: (productName) =>
      productName
        ? `Sua compra de ${productName} foi aprovada.`
        : "Sua compra foi aprovada.",
    accessReady: "Seu acesso está pronto",
    productFallback: "Produto",
    accessEmailGeneric:
      "Você receberá as informações de acesso por e-mail em breve.",
    genericApproved:
      "Seu pagamento foi processado. Verifique seu e-mail para os detalhes da compra e informações de acesso.",
  },
  en: {
    title: "Purchase Approved!",
    subtitle: "Your payment was processed successfully",
    confirmationTitle: "Confirmation Sent",
    confirmationDescription:
      "A confirmation email with all purchase details was sent to the registered address. Please check your inbox in the next few minutes.",
    nextStepsTitle: "Next Steps:",
    nextSteps: [
      "Check your email for the detailed order confirmation",
      "Track your order status through the link we sent",
      "If you have any questions, our support team is here to help",
    ],
    closing:
      "Thank you for your purchase! We hope you have an excellent experience.",
    loadingAccess: "Loading your access...",
    approvedFor: (productName) =>
      productName
        ? `Your purchase of ${productName} was approved.`
        : "Your purchase was approved.",
    accessReady: "Your access is ready",
    productFallback: "Product",
    accessEmailGeneric:
      "You will receive access information by email shortly.",
    genericApproved:
      "Your payment was processed. Check your email for purchase details and access information.",
  },
  es: {
    title: "¡Compra Aprobada!",
    subtitle: "Tu pago se procesó correctamente",
    confirmationTitle: "Confirmación Enviada",
    confirmationDescription:
      "Se envió un correo de confirmación con todos los detalles de tu compra a la dirección registrada. Revisa tu bandeja de entrada en los próximos minutos.",
    nextStepsTitle: "Próximos Pasos:",
    nextSteps: [
      "Revisa tu correo para ver la confirmación detallada del pedido",
      "Sigue el estado de tu pedido desde el enlace enviado",
      "Si tienes alguna duda, nuestro equipo de soporte está disponible",
    ],
    closing:
      "¡Gracias por tu compra! Esperamos que tengas una excelente experiencia.",
    loadingAccess: "Cargando tu acceso...",
    approvedFor: (productName) =>
      productName
        ? `Tu compra de ${productName} fue aprobada.`
        : "Tu compra fue aprobada.",
    accessReady: "Tu acceso está listo",
    productFallback: "Producto",
    accessEmailGeneric:
      "Recibirás la información de acceso por correo electrónico en breve.",
    genericApproved:
      "Tu pago fue procesado. Revisa tu correo para ver los detalles de la compra y la información de acceso.",
  },
};

function getThankYouLocale() {
  const language =
    typeof navigator !== "undefined" ? navigator.language || "" : "";
  if (language.toLowerCase().startsWith("en")) return "en";
  if (language.toLowerCase().startsWith("es")) return "es";
  return "pt";
}

export default function ThankYou() {
  const query = useQuery();
  const content = THANK_YOU_CONTENT[getThankYouLocale()];
  const purchaseId = query.get("pid");
  const token = query.get("t");
  const [receipt, setReceipt] = useState(null);
  const [status, setStatus] = useState("idle");

  useEffect(() => {
    let cancelled = false;

    async function fetchDelivery() {
      if (!purchaseId || !token) {
        setStatus("missing");
        return;
      }

      setStatus("loading");
      try {
        const response = await fetch(
          `${pickApiBase()}/purchases/${encodeURIComponent(
            purchaseId
          )}/delivery?token=${encodeURIComponent(token)}`,
          { cache: "no-store" }
        );
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.error || "Could not load purchase delivery");
        }
        if (!cancelled) {
          setReceipt(data);
          setStatus("success");
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Error loading thank-you delivery:", error);
          setStatus("error");
        }
      }
    }

    fetchDelivery();
    return () => {
      cancelled = true;
    };
  }, [purchaseId, token]);

  const deliverySections =
    receipt?.delivery?.sections?.length > 0
      ? receipt.delivery.sections
      : receipt?.delivery?.links?.length > 0
      ? [
          {
            product: receipt?.product || { name: "" },
            links: receipt.delivery.links,
          },
        ]
      : [];
  const deliveryLinks = deliverySections.flatMap((section) => section.links || []);
  const productName = receipt?.product?.name || "";

  return (
    <div className="min-h-screen w-full bg-background p-6 flex items-center justify-center">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <div className="flex gap-2.5 items-center text-primary">
            <CheckCircle2Icon className="h-8 w-8" />
            <div>
              <h1 className="font-medium text-2xl">{content.title}</h1>
              <p className="text-sm text-muted-foreground mt-1">
                {content.subtitle}
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {status === "loading" && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2Icon className="w-4 h-4 animate-spin" />
              <span>{content.loadingAccess}</span>
            </div>
          )}

          {status === "success" && (
            <>
              <p className="text-muted-foreground">
                {content.approvedFor(productName)}
              </p>

              {deliveryLinks.length > 0 ? (
                <div className="mt-6 rounded-lg border p-4">
                  <h2 className="font-medium mb-3">{content.accessReady}</h2>
                  <div className="space-y-5">
                    {deliverySections.map((section, sectionIndex) => (
                      <div key={`${section.product?.id || "main"}-${sectionIndex}`}>
                        {deliverySections.length > 1 && (
                          <p className="text-sm font-medium mb-2">
                            {section.product?.name || content.productFallback}
                          </p>
                        )}
                        <div className="space-y-3">
                          {(section.links || []).map((link, index) => (
                            <div key={`${link.url}-${index}`}>
                              <Button asChild className="w-full">
                                <a
                                  href={link.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {link.label}
                                  <ExternalLinkIcon className="w-4 h-4 ml-2" />
                                </a>
                              </Button>
                              {link.description && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  {link.description}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="mt-5 text-muted-foreground">
                  {content.accessEmailGeneric}
                </p>
              )}
            </>
          )}

          {(status === "error" || status === "missing") && (
            <p className="text-muted-foreground">
              {content.genericApproved}
            </p>
          )}

          <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-5">
            <h2 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <MailIcon className="w-5 h-5 text-green-600" />
              {content.confirmationTitle}
            </h2>
            <p className="text-sm text-gray-700">
              {content.confirmationDescription}
            </p>
          </div>

          <div className="mt-5 bg-muted/40 rounded-lg p-5">
            <h2 className="font-semibold mb-3">{content.nextStepsTitle}</h2>
            <ul className="space-y-2 text-sm text-muted-foreground">
              {content.nextSteps.map((step) => (
                <li className="flex items-start gap-2" key={step}>
                  <span className="text-primary font-bold mt-0.5">✓</span>
                  <span>{step}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="border-t mt-6 pt-5">
            <p className="text-center text-muted-foreground">
              {content.closing}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
